import type { Form } from '@adornis/forms/form.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { Gender, NamensTitel, Religion } from '../../../db/enums/misc-enums.js';
import { subscribeToConfirmedSBInRegistrationFormTracker } from '../api/confirmSBInRegistrationForm.js';
import { getUserByTrackerToken } from '../api/getUserByTrackerToken.js';
import { RegisterStudentDataSorgeberechtigte, type RegisterStudentData } from '../api/registerStudentWithSuSForm.js';
import { RegistrationField } from './fields/registration-field.js';
import { SgSusEntryFormAddressFields } from './sg-sus-entry-form-address-fields.js';
import { handleNewSb } from './sg-sus-entry-form-handle-new-sb.js';

export const SgSusEntryFormSbFields = stateful((props: { form: Form<RegisterStudentData>; schoolID: string }) => {
  const [manualUpdateForm, setManualUpdateForm] = useState(0);

  const { value: confirmTrackers, loading: confirmTrackersLoading } = useQuery(() => {
    return subscribeToConfirmedSBInRegistrationFormTracker(props.form.document?.activeTrackerIDs ?? [])({
      email: 1,
      confirmed: 1,
      userID: 1,
      token: 1,
    });
  }, [props.form.document?.activeTrackerIDs]);

  const { value: confirmedUsers, loading: confirmedUsersLoading } = useQuery(() => {
    return getUserByTrackerToken(
      confirmTrackers?.filter(tracker => tracker.confirmed).map(tracker => tracker.token ?? '') ?? [],
    )({
      email: 1,
      profilDaten: {
        stammdaten: {
          titel: 1,
          vorname: 1,
          nachname: 1,
        },
        adresse: {
          zip: 1,
          city: 1,
          district: 1,
          streetAndNumber: 1,
        },
      },
    });
  }, [confirmTrackers]);

  const manualUpdate = () => setManualUpdateForm(manualUpdateForm + 1);
  useEffect(() => {
    if (!confirmTrackers) return;
    if (!confirmedUsers) return;

    // we want to convert active trackers which are confirmed to sorgeberechtigte
    const confirmedTrackers = confirmTrackers.filter(tracker => tracker.confirmed);

    confirmedTrackers.forEach(tracker => {
      if (!props.form.document?.sorgeberechtigte.find(sb => sb.email === tracker.email)) {
        const confirmedUser = confirmedUsers.find(user => user.email === tracker.email);
        props.form.pickValueForField(
          ['sorgeberechtigte'],
          [
            ...(props.form.document?.sorgeberechtigte ?? []),
            new RegisterStudentDataSorgeberechtigte({
              email: tracker.email,
              potentialExistingSbID: tracker.userID,
              firstNameSB: confirmedUser?.profilDaten?.stammdaten?.vorname ?? '',
              lastNameSB: confirmedUser?.profilDaten?.stammdaten?.nachname ?? '',
              title: confirmedUser?.profilDaten?.stammdaten?.titel ?? '',
              streetAndNumber: confirmedUser?.profilDaten?.adresse?.streetAndNumber ?? '',
              zipSB: confirmedUser?.profilDaten?.adresse?.zip ?? '',
              citySB: confirmedUser?.profilDaten?.adresse?.city ?? '',
              districtSB: confirmedUser?.profilDaten?.adresse?.district ?? '',
            }),
          ],
        );
      }
    });
  }, [confirmTrackers, confirmedUsers]);

  return html` <section class="card w-full">
    <div class="card-body gap-5 p-4 sm:p-6">
      <h2 class="font-semibold md:text-lg">Sorgeberechtigte</h2>
      ${confirmTrackersLoading || confirmedUsersLoading ? html`<p role="status">Bestätigungen werden geladen …</p>` : nothing}
      ${repeat(
        props.form.document?.sorgeberechtigte ?? [],
        sb => sb,
        (sb, index) => {
          const field = (key: string, options: Parameters<typeof RegistrationField>[2]) =>
            RegistrationField(props.form, ['sorgeberechtigte', index, key], options);
          return html`<section class="flex min-w-0 flex-col gap-5 border-t border-base-300 pt-5">
            <div class="flex items-start justify-between gap-3">
              <h3 class="font-semibold">${index + 1}. Sorgeberechtigte Person</h3>
              ${
                index
                  ? html`<button
                      type="button"
                      class="btn btn-ghost btn-square shrink-0"
                      aria-label=${`${index + 1}. Sorgeberechtigte Person entfernen`}
                      @click=${() => {
                        props.form.pickValueForField(
                          ['sorgeberechtigte'],
                          props.form.document?.sorgeberechtigte.filter((_, i) => i !== index),
                        );
                        manualUpdate();
                      }}
                    >
                      <span class="icon-[material-symbols--delete-outline-rounded] text-xl" aria-hidden="true"></span>
                    </button>`
                  : nothing
              }
            </div>
            <div class="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
              ${field('email', { label: 'E-Mail-Adresse', type: 'email', required: true, disabled: !!sb.potentialExistingSbID, autocomplete: `section-guardian${index} email` })}
              ${field('title', { label: 'Titel', options: Object.values(NamensTitel) })}
              ${field('firstNameSB', { label: 'Vorname', required: true, autocomplete: `section-guardian${index} given-name` })}
              ${field('lastNameSB', { label: 'Nachname', required: true, autocomplete: `section-guardian${index} family-name` })}
              ${field('geschlecht', { label: 'Geschlecht', required: true, options: Object.values(Gender) })}
              ${field('religion', { label: 'Religion', required: true, options: Object.values(Religion) })}
              ${field('telefonNr', { label: 'Telefonnummer', type: 'tel', required: true, autocomplete: `section-guardian${index} tel` })}
              ${field('jobtitle', { label: 'Beruf' })}
            </div>
            <div class="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h4 class="font-semibold">Adresse</h4>
              <button
                type="button"
                class="btn btn-outline h-auto min-h-11 whitespace-normal text-left"
                @click=${() => {
                  props.form.pickValueForField(
                    ['sorgeberechtigte', index, 'streetAndNumber'],
                    props.form.document?.streetAndNumber,
                  );
                  props.form.pickValueForField(['sorgeberechtigte', index, 'zipSB'], props.form.document?.zip);
                  props.form.pickValueForField(['sorgeberechtigte', index, 'citySB'], props.form.document?.city);
                  props.form.pickValueForField(
                    ['sorgeberechtigte', index, 'districtSB'],
                    props.form.document?.district,
                  );
                  manualUpdate();
                }}
              >
                Adresse des Kindes übernehmen
              </button>
            </div>
            ${SgSusEntryFormAddressFields({ form: props.form, prefix: ['sorgeberechtigte', index], guardian: true })}
          </section>`;
        },
      )}
      ${repeat(
        confirmTrackers ?? [],
        tracker => tracker.email,
        tracker =>
          tracker.confirmed
            ? nothing
            : html`<div class="alert alert-warning alert-soft text-base-content" role="status">
                <div class="min-w-0">
                  <p class="font-semibold break-all">${tracker.email}</p>
                  <p>
                    Die E-Mail-Adresse wurde noch nicht bestätigt. Die Person kann die Bestätigung auch nach der
                    Bewerbung vornehmen.
                  </p>
                </div>
              </div>`,
      )}
      <button
        type="button"
        class="btn btn-outline h-auto min-h-11 w-full whitespace-normal"
        @click=${async () => {
          const result = await handleNewSb(props.schoolID);
          if (result === undefined) return;
          if (result === null)
            props.form.pickValueForField(
              ['sorgeberechtigte'],
              [...(props.form.document?.sorgeberechtigte ?? []), new RegisterStudentDataSorgeberechtigte({})],
            );
          else
            props.form.pickValueForField(
              ['activeTrackerIDs'],
              [...(props.form.document?.activeTrackerIDs ?? []), result],
            );
          manualUpdate();
        }}
      >
        <span class="icon-[material-symbols--add-rounded] text-xl shrink-0" aria-hidden="true"></span>Weitere
        Sorgeberechtigte hinzufügen
      </button>
    </div>
  </section>`;
});
